import { useState, type ReactNode } from "react";
import { useCopy } from "../../../shared/i18n";

/**
 * The compulsory first enrolment, shared by both sign-in doors.
 *
 * 🔴 IT IS ONE COMPONENT BECAUSE IT IS ONE FLOW, AND BOTH DOORS REACH IT.
 * `OperatorLoginScreen` is where an operator normally arrives, but every staff
 * door POSTs the same `/auth/login` and stays publicly reachable — so an
 * operator who bookmarked the school door, or the teacher one at
 * `#/teacher/signin`, would otherwise hit a challenge nothing rendered and a
 * button that spun forever. Two copies of a screen that mints a second factor is
 * also two places for the "scan first, enable second" ordering to drift.
 *
 * ⚠️ THE WRONG-DOOR CHECK DOES NOT REMOVE THAT NEED. `finishSignInAtDoor` runs
 * strictly AFTER authentication — and enrolment is part of authenticating, so it
 * happens first, at whichever door the person actually used.
 *
 * 🔴 IT HOLDS NO TOKEN AND NO PASSWORD. The router keeps the fifteen-minute
 * enrolment assertion; this has a QR to draw, a text box, and two callbacks.
 *
 * ⚠️ THE CHROME IS THE CALLER'S. Each door has its own shell — the operator's
 * dark panel, the staff form's `AuthShell` — so this renders a body and the
 * caller wraps it. The copy is here rather than in either screen's dictionary
 * precisely so the two cannot describe the same flow differently.
 */

export interface MfaEnrolmentData {
  secret: string;
  qrSvg: string;
  otpauthUri: string;
}

export function useMfaEnrolmentCopy() {
  return useCopy({
    en: {
      enrolTitle: "Set up your authenticator",
      /**
       * 🔴 IT NAMES WHAT THIS ACCOUNT CAN REACH, NOT "SECURITY MATTERS". An
       * operator account has no school of its own and can open every school on
       * Kalqy — that is what makes a minute of setup obviously worth it, and a
       * generic sentence about protection is not.
       */
      enrolWhy:
        "Operator accounts can open every school on Kalqy, so a password on its own is not enough. This takes about a minute and you only do it once.",
      enrolStep1:
        "Install an authenticator app if you do not have one — Google Authenticator, Authy and 1Password all work.",
      enrolStep2: "Scan this code with it.",
      enrolManual: "Cannot scan? Enter this setup key by hand instead.",
      enrolSecretLabel: "Setup key",
      enrolCodeLabel: "Now type the 6-digit code the app shows",
      enrolSubmit: "Turn it on and sign in",
      enrolChecking: "Checking…",
      enrolPreparing: "Preparing…",
      enrolBack: "Start again",
      enrolRefused:
        "That code did not match. Wait for the next one and try again.",
      enrolFailed: "Could not finish the setup. Go back and sign in again.",
      throttled: "Too many attempts from here. Wait a minute and try again.",
      recoveryTitle: "Save your recovery codes",
      /* The same three-sentence shape the school-admin credential panel uses,
         because the constraint is identical: this is the only copy. */
      recoveryBody:
        "Each of these signs you in once if you lose your phone. Save them somewhere that is not your phone — this is the only time they are shown.",
      recoveryWarn:
        "Without them, a lost phone means another operator has to rebuild your account.",
      recoveryDone: "I have saved them — continue",
    },
  });
}

export interface MfaEnrolmentBodyProps {
  enrolment: MfaEnrolmentData | null;
  code: string;
  onCode: (value: string) => void;
  busy: boolean;
  /** `'code'` when the code was refused, `'throttled'`, `'failed'`, or null. */
  problem: "code" | "throttled" | "failed" | null;
  onSubmit: () => void;
  onBack: () => void;
  /** The caller's field and button classes, so each door keeps its own chrome. */
  fieldClass: string;
  submitClass: string;
  /** The caller's `role="alert"` box. */
  renderAlert: (children: ReactNode, testId?: string) => ReactNode;
  idPrefix: string;
}

export function MfaEnrolmentBody({
  enrolment,
  code,
  onCode,
  busy,
  problem,
  onSubmit,
  onBack,
  fieldClass,
  submitClass,
  renderAlert,
  idPrefix,
}: MfaEnrolmentBodyProps) {
  const t = useMfaEnrolmentCopy();

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <h2 className="font-dash text-dash-lg font-extrabold text-ink">
        {t.enrolTitle}
      </h2>
      <p className="mt-2 max-w-[38ch] font-dash text-dash-sm text-muted">
        {t.enrolWhy}
      </p>

      <ol className="mt-5 flex flex-col gap-2 font-dash text-dash-sm text-ink">
        <li>{t.enrolStep1}</li>
        <li>{t.enrolStep2}</li>
      </ol>

      {enrolment === null ? (
        <p className="mt-4 font-dash text-dash-sm text-muted">
          {t.enrolPreparing}
        </p>
      ) : (
        <>
          {/*
            🔴 AN INLINE SVG FROM OUR OWN SERVER, NEVER AN <img> AT A QR SERVICE.
            Every hosted generator works by being sent the payload — which here
            is a live TOTP secret — so the whole second factor would travel to a
            third party in a query string.

            ⚠️ `dangerouslySetInnerHTML` IS THE RIGHT TOOL AND THE RISK IS
            BOUNDED: `qr.ts` emits one <rect> and one <path> of machine-generated
            coordinates and never interpolates the payload into its output.
          */}
          <div
            className="mt-4 inline-block rounded-lg border border-line bg-white p-2"
            aria-hidden="true"
            dangerouslySetInnerHTML={{ __html: enrolment.qrSvg }}
          />

          <p className="mt-3 font-dash text-dash-xs text-muted">
            {t.enrolManual}
          </p>
          <p className="font-dash text-dash-xs font-bold text-muted">
            {t.enrolSecretLabel}
          </p>
          {/* `select-all` so it is copied in one gesture rather than partially
              dragged — there is no second copy to check a typo against. */}
          <p className="tabular mt-1 select-all rounded-lg border border-line bg-canvas px-3 py-2 font-dash text-dash-sm font-bold tracking-wider text-ink">
            {enrolment.secret}
          </p>
        </>
      )}

      <label
        className="mt-5 block font-dash text-dash-xs font-bold text-muted"
        htmlFor={`${idPrefix}-enrol-code`}
      >
        {t.enrolCodeLabel}
      </label>
      <input
        id={`${idPrefix}-enrol-code`}
        /* 🔴 `text`, NOT `number`. A number input silently strips the leading
           zero that one code in ten begins with; `inputMode` still opens the
           digit keypad on a phone. */
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={64}
        value={code}
        onChange={(e) => {
          onCode(e.target.value);
        }}
        className={`${fieldClass} tracking-[0.3em]`}
      />

      <button
        type="submit"
        disabled={busy || enrolment === null || code.trim().length === 0}
        className={submitClass}
      >
        {busy ? t.enrolChecking : t.enrolSubmit}
      </button>

      {problem === "code" &&
        renderAlert(t.enrolRefused, `${idPrefix}-enrol-refused`)}
      {problem === "throttled" && renderAlert(t.throttled)}
      {problem === "failed" && renderAlert(t.enrolFailed)}

      <p className="mt-5 text-center">
        <button
          type="button"
          onClick={onBack}
          className="rounded-pill px-3 py-1.5 font-dash text-dash-xs font-bold text-ink-soft underline decoration-line-strong underline-offset-4 transition-colors duration-150 ease-dash hover:text-ink"
        >
          {t.enrolBack}
        </button>
      </p>
    </form>
  );
}

/**
 * The recovery codes, shown once.
 *
 * 🔴 A WALL, ON PURPOSE, AND THE LAST SCREEN BEFORE THE PLATFORM. The operator
 * has just made a second factor compulsory for themselves; these ten strings are
 * the only thing between a dropped phone and an account another operator has to
 * rebuild. There is no skip and no way past except the acknowledgement — the
 * router deliberately does not navigate after `finishEnrolment` for exactly this
 * reason.
 */
export function RecoveryCodesPanel({
  codes,
  onDone,
  submitClass,
}: {
  codes: readonly string[];
  onDone: () => void;
  submitClass: string;
}) {
  const t = useMfaEnrolmentCopy();

  return (
    <div
      className="rounded-2xl p-8 shadow-card"
      style={{
        background: "var(--color-pill-warning-bg)",
        border: "1px solid var(--color-pill-warning-line)",
      }}
    >
      <h2
        className="font-dash text-dash-lg font-extrabold"
        style={{ color: "var(--color-pill-warning-fg)" }}
      >
        {t.recoveryTitle}
      </h2>
      <p
        className="mt-2 font-dash text-dash-sm"
        style={{ color: "var(--color-pill-warning-fg)" }}
      >
        {t.recoveryBody}
      </p>

      <ul className="tabular mt-4 grid select-all grid-cols-2 gap-x-6 gap-y-1 rounded-lg border border-line bg-surface p-4 font-dash text-dash-sm font-bold text-ink">
        {codes.map((one) => (
          <li key={one}>{one}</li>
        ))}
      </ul>

      <p
        className="mt-3 font-dash text-dash-xs"
        style={{ color: "var(--color-pill-warning-fg)" }}
      >
        {t.recoveryWarn}
      </p>

      <button type="button" onClick={onDone} className={submitClass}>
        {t.recoveryDone}
      </button>
    </div>
  );
}

/**
 * The state both doors keep for the enrolment flow, so neither has to reinvent
 * the ordering (start → scan → confirm → save codes → in).
 */
export function useMfaEnrolmentFlow() {
  const [enrolment, setEnrolment] = useState<MfaEnrolmentData | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  return { enrolment, setEnrolment, recoveryCodes, setRecoveryCodes };
}
